import { Button } from "@nimit9/signet-ui/components/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@nimit9/signet-ui/components/select"
import type { ParsedStatement } from "@paisa/types"
import { useCurrencyFormat } from "@/hooks/use-currency-format"
import { BankChip } from "./bank-chip"
import { CreateAccountInline } from "./create-account-inline"
import { accountTypeLabel, sumByType } from "./helpers"
import type { AccountOption } from "./types"

interface StepPreviewProps {
  parsed: ParsedStatement
  accounts: AccountOption[]
  selectedAccountId: string | null
  isAutoMatched: boolean
  onSelectAccount: (id: string | null) => void
  onContinue: () => void
  onBack: () => void
}

export function StepPreview({
  parsed,
  accounts,
  selectedAccountId,
  isAutoMatched,
  onSelectAccount,
  onContinue,
  onBack,
}: StepPreviewProps) {
  const fmt = useCurrencyFormat()
  const moneyIn = sumByType(parsed.transactions, "credit")
  const moneyOut = sumByType(parsed.transactions, "debit")
  const netChange = moneyIn - moneyOut
  const selectedAccount = accounts.find((a) => a.id === selectedAccountId)

  return (
    <>
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <p className="font-mono text-[10.5px] uppercase tracking-widest text-accent-amber mb-1">
          Step 2 of 3
        </p>
        <h3 className="text-[20px] font-bold tracking-tight text-text-primary mb-1">
          Does this look right?
        </h3>
        <p className="text-[12.5px] text-text-muted mb-5 leading-relaxed">
          We read your file on this device. Pick which account in Paisa it belongs to.
        </p>

        {/* Summary card */}
        <div className="mb-4 rounded-[12px] border border-border-subtle bg-bg-elevated p-3.5">
          {/* Bank header */}
          <div className="mb-3 flex items-center gap-2.5">
            <BankChip bank={parsed.bank} size="sm" />
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold tracking-tight text-text-primary">
                {parsed.bank.toUpperCase()} · {accountTypeLabel(parsed.accountType)}
              </p>
              {parsed.accountNumber && (
                <p className="mt-0.5 font-mono text-[10px] text-text-muted">
                  …{parsed.accountNumber}
                </p>
              )}
            </div>
          </div>

          {/* Stats 2×2 */}
          <div
            className="grid grid-cols-2 gap-px overflow-hidden rounded-[8px] border border-border-subtle"
            style={{ background: "var(--border-subtle)" }}
          >
            {[
              { k: "Txns", v: String(parsed.transactions.length), tone: null },
              {
                k: "Net",
                v: (netChange >= 0 ? "+" : "−") + fmt(Math.abs(netChange)),
                tone: netChange >= 0 ? "green" : "red",
              },
              { k: "In", v: fmt(moneyIn), tone: "green" },
              { k: "Out", v: fmt(moneyOut), tone: "red" },
            ].map(({ k, v, tone }) => (
              <div key={k} className="bg-bg-elevated px-2.5 py-2.5">
                <p className="text-[10px] uppercase tracking-widest text-text-muted">{k}</p>
                <p
                  className="mt-0.5 text-[15px] font-bold tracking-tight"
                  style={{
                    color:
                      tone === "green"
                        ? "var(--accent-green)"
                        : tone === "red"
                          ? "var(--money-debit)"
                          : "var(--text-primary)",
                  }}
                >
                  {v}
                </p>
              </div>
            ))}
          </div>

          {/* Date range */}
          {(parsed.periodStart || parsed.periodEnd) && (
            <div className="mt-2.5 flex items-center justify-between text-[10.5px] text-text-muted">
              <span className="font-mono">{parsed.periodStart ?? "—"}</span>
              <span className="font-mono">→ {parsed.periodEnd ?? "—"}</span>
            </div>
          )}
        </div>

        {/* Account link card */}
        <Select
          value={selectedAccountId ?? ""}
          onValueChange={(v: string | null) => onSelectAccount(v || null)}
        >
          <SelectTrigger
            className="mb-2 !h-auto !whitespace-normal !items-start w-full rounded-[12px] px-3 py-3 text-left"
            style={{
              borderColor: "rgba(245,158,11,0.22)",
              background: "rgba(245,158,11,0.06)",
            }}
          >
            <div className="min-w-0 flex-1">
              <p
                className="text-[10px] uppercase tracking-widest mb-1.5"
                style={{ color: "var(--accent-amber)" }}
              >
                Link to account
              </p>
              <p className="text-[13px] font-semibold text-text-primary truncate">
                {selectedAccount
                  ? `${selectedAccount.name} · ···${selectedAccount.last4}`
                  : "Select account…"}
              </p>
              <p className="mt-1.5 text-[10.5px] text-text-muted">
                {isAutoMatched ? "Auto-matched · tap to change" : "No match found · tap to select"}
              </p>
            </div>
          </SelectTrigger>
          <SelectContent>
            {accounts.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.name} · {a.bankName} · ···{a.last4}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {!selectedAccountId && (
          <CreateAccountInline parsed={parsed} onCreated={(id) => onSelectAccount(id)} />
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-border-subtle px-6 py-3.5">
        <Button variant="ghost" size="sm" onClick={onBack}>
          Back
        </Button>
        <Button size="sm" disabled={!selectedAccountId} onClick={onContinue}>
          Review {parsed.transactions.length} rows →
        </Button>
      </div>
    </>
  )
}
